/**
 * The content-addressed asset and font libraries the render plan resolves against.
 *
 * `m01b-assets.js` builds these at runtime: the seed generates its five demo backgrounds as
 * deterministic SVG, ingests the bytes, and the library keys them by `asset_<sha256[0..24]>`.
 *
 * WHO FILLS THE INDEX is decided by whoever is mounting the screens, not by this module. The
 * visual acceptance demo registers `data/demo-assets.json`, which carries the library exactly as
 * the running reference held it (the demo cannot re-run the ingestion: `detectAlpha` needs a canvas
 * and `sha256` needs `crypto.subtle`). The product registers what `GET /assets` and `GET /fonts`
 * answer. Importing the demo's file here would have put five synthetic backgrounds into the
 * production bundle for no reason at all.
 *
 * `resolveRef` is fail-closed on the digest, as upstream: a reference whose sha256 no longer
 * matches resolves to an error, never to the stale asset.
 */

export interface RenderAsset {
  id: string;
  sha256: string;
  mimeType: string;
  byteSize: number;
  width: number;
  height: number;
  aspectRatio: number;
  hasAlpha: boolean;
  originalFileName: string;
  dataUrlForPrototype: string;
}

export interface RenderFont {
  id: string;
  sha256: string;
  family: string;
  weight: number;
  style: string;
}

export interface AssetRef {
  assetId?: string | undefined;
  sha256?: string | undefined;
}

export interface FontRef {
  fontAssetId?: string | undefined;
  sha256?: string | undefined;
}

export type ResolveResult<T> =
  | { ok: true; asset: T }
  | { ok: false; reason: string; expected?: string; actual?: string };

const assetIndex = new Map<string, RenderAsset>();
const fontIndex = new Map<string, RenderFont>();

/**
 * Fills the index. Idempotent and additive: registering the same asset twice replaces it with the
 * same record, and nothing is ever removed, because a render plan that resolved once must keep
 * resolving for as long as the page lives.
 */
export function registerRenderLibrary(library: { assets?: RenderAsset[]; fonts?: RenderFont[] }): void {
  for (const a of library.assets ?? []) assetIndex.set(a.id, a);
  for (const f of library.fonts ?? []) fontIndex.set(f.id, f);
}

export const assetById = (id: string): RenderAsset | null => assetIndex.get(id) ?? null;
export const allAssets = (): RenderAsset[] => Array.from(assetIndex.values());
export const allFonts = (): RenderFont[] => Array.from(fontIndex.values());

export function resolveAssetRef(ref: AssetRef | null | undefined): ResolveResult<RenderAsset> {
  if (!ref?.assetId) return { ok: false, reason: 'missing_ref' };
  const a = assetIndex.get(ref.assetId);
  if (!a) return { ok: false, reason: 'not_found' };
  if (ref.sha256 && a.sha256 !== ref.sha256) {
    return { ok: false, reason: 'digest_mismatch', expected: ref.sha256, actual: a.sha256 };
  }
  return { ok: true, asset: a };
}

export function resolveFontRef(ref: FontRef | null | undefined): ResolveResult<RenderFont> {
  if (!ref?.fontAssetId) return { ok: false, reason: 'missing_ref' };
  const f = fontIndex.get(ref.fontAssetId);
  if (!f) return { ok: false, reason: 'not_found' };
  if (ref.sha256 && f.sha256 !== ref.sha256) {
    return { ok: false, reason: 'digest_mismatch', expected: ref.sha256, actual: f.sha256 };
  }
  return { ok: true, asset: f };
}
